/**
 * Helper functions for working with compound-keyed state maps.
 *
 * Compound key format:
 *   bitmask:     "bankId:liftId:Type:Value"  (or "bankId::Type:Value" for bank-only)
 *   position_map: "bankId:liftId"             (or "bankId:" for bank-only)
 *   service_map:  "bankId:liftId"             (or "bankId:" for bank-only)
 */

import { UuidT } from "../flatbuffers/generated/front-end/uuid";
import { bitmaskPrefix as typedBitmaskPrefix, compoundKey, oneHotKey } from "./typedKeys";

/** Build the compound prefix used for bitmask lookups: "bankId:liftId:" */
export function bitmaskPrefix(bankId: UuidT, liftId: UuidT): string {
  return typedBitmaskPrefix(bankId, liftId);
}

/** Build the compound key used for position_map and service_map: "bankId:liftId" */
export function liftKey(bankId: UuidT, liftId: UuidT): string {
  return compoundKey(bankId, liftId);
}

/**
 * Extract the message suffix (e.g. "Usage:Up") from a compound bitmask key
 * like "bankId:liftId:Usage:Up".
 *
 * The first two colon-separated segments are bankId and liftId; the rest is the suffix.
 */
export function extractSuffix(compoundBitmaskKey: string): string {
  const firstColon = compoundBitmaskKey.indexOf(":");
  if (firstColon === -1) return compoundBitmaskKey;
  const secondColon = compoundBitmaskKey.indexOf(":", firstColon + 1);
  if (secondColon === -1) return "";
  return compoundBitmaskKey.substring(secondColon + 1);
}

/**
 * Get all bitmask entries for a specific bank:lift, returning a flat map
 * keyed by suffix (e.g. "Usage:Up" → true).
 */
export function getBitmaskForLift(
  bitmask: Record<string, boolean>,
  bankId: UuidT,
  liftId: UuidT,
): Record<string, boolean> {
  const prefix = bitmaskPrefix(bankId, liftId);
  const result: Record<string, boolean> = {};
  Object.keys(bitmask).forEach((key) => {
    if (key.startsWith(prefix)) {
      result[key.substring(prefix.length)] = bitmask[key];
    }
  });
  return result;
}

/** Get position for a specific bank:lift from the position map */
export function getPosition(
  positionMap: Record<string, number>,
  bankId: UuidT,
  liftId: UuidT,
): number {
  return positionMap[liftKey(bankId, liftId)] ?? 0;
}

/** Get service mode name for a specific bank:lift from the service map */
export function getService(
  serviceMap: Record<string, string>,
  bankId: UuidT,
  liftId: UuidT,
): string | null {
  return serviceMap[liftKey(bankId, liftId)] ?? null;
}

/**
 * One entry of a one-hot state map: the value and whether it is currently set.
 */
export interface OneHotEntry {
  active: boolean;
  messageType: number;
}

/** Get oneHot entry for a specific bank:lift:category from the oneHotMap */
export function getOneHot(
  oneHotMap: Record<string, OneHotEntry>,
  bankId: UuidT,
  liftId: UuidT,
  category: number,
): OneHotEntry | null {
  return oneHotMap[oneHotKey(bankId, liftId, category)] ?? null;
}
